/**
 * FileUpload — 파일 업로드 드롭존 컴포넌트
 *
 * 클릭 또는 드래그&드롭으로 파일을 선택할 수 있다.
 * 이미지 파일의 경우 썸네일 프리뷰를 표시한다.
 */

import { useCallback, useRef, useState, type DragEvent } from 'react'
import { cn } from '../../lib/cn.js'
import styles from './FileUpload.module.css'

export interface FileUploadProps {
  /** 필드 레이블 */
  label?: string
  /** 에러 메시지 */
  error?: string
  /** 보조 안내 텍스트 */
  helperText?: string
  /** 허용 파일 타입 (input accept). 기본값 image/* */
  accept?: string
  /** 다중 파일 허용 여부 */
  multiple?: boolean
  /** 최대 파일 수 (multiple일 때) */
  maxFiles?: number
  /** 드롭존 안내 텍스트 */
  placeholder?: string
  /** 선택된 파일 목록 */
  files?: File[]
  /** 프리뷰 URL 목록 (외부 제어 시) */
  previews?: string[]
  /** 파일 선택 콜백 */
  onChange?: (files: File[], previews: string[]) => void
  /** 비활성 상태 */
  disabled?: boolean
  /** 추가 클래스명 */
  className?: string
}

export function FileUpload({
  label,
  error,
  helperText,
  accept = 'image/*',
  multiple = false,
  maxFiles = 5,
  placeholder,
  files: controlledFiles,
  previews: controlledPreviews,
  onChange,
  disabled = false,
  className,
}: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const [internalFiles, setInternalFiles] = useState<File[]>([])
  const [internalPreviews, setInternalPreviews] = useState<string[]>([])

  const files = controlledFiles ?? internalFiles
  const previews = controlledPreviews ?? internalPreviews

  const isImage = accept?.includes('image')
  const defaultPlaceholder = multiple
    ? `클릭하거나 파일을 드래그하세요 (최대 ${maxFiles}장)`
    : '클릭하거나 파일을 드래그하세요'

  const processFiles = useCallback((incoming: File[]) => {
    const limited = multiple ? incoming.slice(0, maxFiles) : incoming.slice(0, 1)

    // 이미지 프리뷰 생성
    if (isImage) {
      const newPreviews: string[] = []
      let loaded = 0
      limited.forEach((file, idx) => {
        const reader = new FileReader()
        reader.onload = () => {
          newPreviews[idx] = reader.result as string
          loaded++
          if (loaded === limited.length) {
            setInternalFiles(limited)
            setInternalPreviews([...newPreviews])
            onChange?.(limited, [...newPreviews])
          }
        }
        reader.readAsDataURL(file)
      })
      if (limited.length === 0) {
        setInternalFiles([])
        setInternalPreviews([])
        onChange?.([], [])
      }
    } else {
      setInternalFiles(limited)
      setInternalPreviews([])
      onChange?.(limited, [])
    }
  }, [multiple, maxFiles, isImage, onChange])

  const handleClick = useCallback(() => {
    if (!disabled) inputRef.current?.click()
  }, [disabled])

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(Array.from(e.target.files))
    }
  }, [processFiles])

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault()
    if (!disabled) setDragOver(true)
  }, [disabled])

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault()
    setDragOver(false)
  }, [])

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (disabled) return
    const dropped = Array.from(e.dataTransfer.files)
    if (dropped.length > 0) processFiles(dropped)
  }, [disabled, processFiles])

  const handleRemove = useCallback((index: number) => {
    const nextFiles = files.filter((_, i) => i !== index)
    const nextPreviews = previews.filter((_, i) => i !== index)
    setInternalFiles(nextFiles)
    setInternalPreviews(nextPreviews)
    onChange?.(nextFiles, nextPreviews)
  }, [files, previews, onChange])

  return (
    <div className={cn(styles.wrapper, error && styles.error, className)}>
      {label && <label className={styles.label}>{label}</label>}

      <div
        className={cn(
          styles.dropzone,
          dragOver && styles.dragOver,
          disabled && styles.disabled,
          error && styles.dropzoneError,
        )}
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick() }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleInputChange}
          className={styles.hiddenInput}
          disabled={disabled}
        />

        {files.length === 0 ? (
          <div className={styles.placeholderContent}>
            <svg className={styles.uploadIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M12 16V4m0 0L8 8m4-4l4 4" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M2 17l.621 2.485A2 2 0 004.561 21h14.878a2 2 0 001.94-1.515L22 17" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className={styles.placeholderText}>{placeholder ?? defaultPlaceholder}</span>
          </div>
        ) : (
          <div className={styles.previewGrid}>
            {previews.map((src, i) => (
              <div key={i} className={styles.previewItem}>
                {isImage ? (
                  <img src={src} alt={files[i]?.name ?? `파일 ${i + 1}`} className={styles.previewThumb} />
                ) : (
                  <span className={styles.fileName}>{files[i]?.name}</span>
                )}
                <button
                  type="button"
                  className={styles.removeBtn}
                  onClick={(e) => { e.stopPropagation(); handleRemove(i) }}
                  aria-label="삭제"
                >
                  <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
                    <path d="M4.646 4.646a.5.5 0 01.708 0L8 7.293l2.646-2.647a.5.5 0 01.708.708L8.707 8l2.647 2.646a.5.5 0 01-.708.708L8 8.707l-2.646 2.647a.5.5 0 01-.708-.708L7.293 8 4.646 5.354a.5.5 0 010-.708z"/>
                  </svg>
                </button>
              </div>
            ))}
            {multiple && files.length < maxFiles && (
              <div className={styles.addMore}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20">
                  <path d="M12 5v14m-7-7h14" strokeLinecap="round" />
                </svg>
              </div>
            )}
          </div>
        )}
      </div>

      {error && <span className={styles.errorText}>{error}</span>}
      {!error && helperText && <span className={styles.helperText}>{helperText}</span>}
    </div>
  )
}
